import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { 
  Upload, 
  File, 
  X, 
  Check, 
  AlertTriangle, 
  Loader2,
  FileText,
  Image,
  FileSpreadsheet,
  Code
} from 'lucide-react';
import { 
  processUploadedFile, 
  ProcessedFile, 
  formatFileSize, 
  getFileTypeIcon,
  SUPPORTED_FILE_TYPES,
  MAX_FILE_SIZE
} from '@/lib/fileUploadUtils';
import { useToast } from '@/hooks/use-toast';

interface FileUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFilesProcessed: (files: ProcessedFile[]) => void;
  maxFiles?: number;
  title?: string;
  description?: string;
}

interface UploadedFileState {
  file: File;
  processed?: ProcessedFile;
  status: 'pending' | 'processing' | 'success' | 'error';
  error?: string;
  progress: number;
}

export const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
  open,
  onOpenChange,
  onFilesProcessed,
  maxFiles = 5,
  title = "Upload Files",
  description = "Upload documents, spreadsheets, images, and code files to include in your conversation."
}) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileState[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setUploadedFiles([]);
      setIsDragOver(false);
    }
    onOpenChange(newOpen);
  };

  // Handle file selection
  const handleFileSelect = useCallback(async (files: File[]) => {
    const newFiles = files.slice(0, maxFiles - uploadedFiles.length);
    
    if (newFiles.length < files.length) {
      toast({
        title: "File Limit Reached",
        description: `Only ${maxFiles} files can be uploaded at once. ${newFiles.length} files have been selected.`,
        variant: "default"
      });
    }

    // Add files to state with pending status
    const newFileStates: UploadedFileState[] = newFiles.map(file => ({
      file,
      status: 'pending',
      progress: 0
    }));

    setUploadedFiles(prev => [...prev, ...newFileStates]);

    // Process each file
    for (let i = 0; i < newFiles.length; i++) {
      const fileIndex = uploadedFiles.length + i;
      
      setUploadedFiles(prev => prev.map((fileState, index) => 
        index === fileIndex 
          ? { ...fileState, status: 'processing', progress: 50 }
          : fileState
      ));

      try {
        const result = await processUploadedFile(newFiles[i]);
        
        if (result.success && result.file) {
          setUploadedFiles(prev => prev.map((fileState, index) => 
            index === fileIndex 
              ? { 
                  ...fileState, 
                  processed: result.file,
                  status: 'success', 
                  progress: 100 
                }
              : fileState
          ));
        } else {
          setUploadedFiles(prev => prev.map((fileState, index) => 
            index === fileIndex 
              ? { 
                  ...fileState, 
                  status: 'error', 
                  error: result.error || 'Failed to process file',
                  progress: 0 
                }
              : fileState
          ));
        }
      } catch (error) {
        setUploadedFiles(prev => prev.map((fileState, index) => 
          index === fileIndex 
            ? { 
                ...fileState, 
                status: 'error', 
                error: error instanceof Error ? error.message : 'Failed to process file',
                progress: 0 
              }
            : fileState
        ));
      }
    }
  }, [uploadedFiles.length, maxFiles, toast]);

  // Handle file input change
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      handleFileSelect(files);
    }
    // Reset input value
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag and drop
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  // Remove file
  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Get file type icon component
  const getFileTypeIconComponent = (filename: string) => {
    const extension = '.' + filename.split('.').pop()?.toLowerCase();
    
    if (['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(extension)) {
      return <Image className="h-4 w-4" />;
    }
    if (['.xlsx', '.xls', '.csv'].includes(extension)) {
      return <FileSpreadsheet className="h-4 w-4" />;
    }
    if (['.js', '.ts', '.py', '.java', '.cpp', '.html', '.css', '.json'].includes(extension)) {
      return <Code className="h-4 w-4" />;
    }
    return <FileText className="h-4 w-4" />;
  };

  // Handle adding files to conversation
  const handleAddToConversation = () => {
    const successfulFiles = uploadedFiles
      .filter(fileState => fileState.status === 'success' && fileState.processed)
      .map(fileState => fileState.processed!);
    
    if (successfulFiles.length > 0) {
      onFilesProcessed(successfulFiles);
      toast({
        title: "Files Added",
        description: `${successfulFiles.length} file(s) have been added to your conversation.`,
        variant: "default"
      });
      handleOpenChange(false);
    } else {
      toast({
        title: "No Files Ready",
        description: "Please wait for files to finish processing or fix any errors.",
        variant: "destructive"
      });
    }
  };

  // Get supported file types list
  const getSupportedFileTypes = () => {
    const extensions = Object.values(SUPPORTED_FILE_TYPES);
    return extensions.slice(0, 10).join(', ') + (extensions.length > 10 ? ', and more...' : '');
  };

  const hasSuccessfulFiles = uploadedFiles.some(file => file.status === 'success');
  const isProcessing = uploadedFiles.some(file => file.status === 'processing');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {title}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            {description}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragOver 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-lg font-medium mb-2">
              Drop files here or click to browse
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Maximum {maxFiles} files, up to {Math.round(MAX_FILE_SIZE / (1024 * 1024))}MB each
            </p>
            <p className="text-xs text-gray-400">
              Supported: {getSupportedFileTypes()}
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
            accept={Object.keys(SUPPORTED_FILE_TYPES).join(',')}
          />

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              <Separator />
              <h4 className="font-medium">Uploaded Files</h4>
              <ScrollArea className="max-h-60">
                <div className="space-y-2">
                  {uploadedFiles.map((fileState, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50"
                    >
                      <div className="flex-shrink-0">
                        {getFileTypeIconComponent(fileState.file.name)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {fileState.file.name}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-gray-500">
                            {formatFileSize(fileState.file.size)}
                          </p>
                          
                          {fileState.status === 'success' && fileState.processed?.metadata && (
                            <>
                              {fileState.processed.metadata.wordCount && (
                                <Badge variant="secondary" className="text-xs">
                                  {fileState.processed.metadata.wordCount} words
                                </Badge>
                              )}
                              {fileState.processed.metadata.sheets && (
                                <Badge variant="secondary" className="text-xs">
                                  {fileState.processed.metadata.sheets.length} sheets
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                        
                        {fileState.status === 'processing' && (
                          <Progress value={fileState.progress} className="mt-2 h-1" />
                        )}
                        
                        {fileState.status === 'error' && (
                          <p className="text-xs text-red-600 mt-1">
                            {fileState.error}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {fileState.status === 'processing' && (
                          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                        )}
                        {fileState.status === 'success' && (
                          <Check className="h-4 w-4 text-green-500" />
                        )}
                        {fileState.status === 'error' && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFile(index)}
                          className="h-6 w-6 p-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            
            <Button 
              onClick={handleAddToConversation}
              disabled={!hasSuccessfulFiles || isProcessing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                `Add ${uploadedFiles.filter(f => f.status === 'success').length} file(s) to conversation`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 